import { AccountTree, Edit, OpenInNew } from '@mui/icons-material';
import {
  Alert,
  Box,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, deDE, type GridColDef } from '@mui/x-data-grid';
import debounce from '@mui/material/utils/debounce';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../core/standardFunctions';
import { useGetLegalRequirementDependencyOverview } from '../../hooks/reactQuerys/LegalDependencyQueries';
import type {
  ILegalRequirementDependencyAction,
  ILegalRequirementDependencyCondition,
  ILegalRequirementDependencyOverviewEntry,
} from '../../Interfaces/ILegalDependency';
import {
  getActionRuleId,
  getConditionGroupId,
  getRuleName,
  getRuleTransactionId,
} from './LegalDependency/utils';

interface ILegalDependencyOverviewRow {
  id: number;
  actionCount: number;
  conditionCount: number;
  Executed_at: string | null;
  groupCount: number;
  name: string;
  rootTransactionId: number | null;
  rootTransactionLabel: string;
  ruleId: number;
  statusLabel: string;
  Updated_at: string | null;
  Created_at: string | null;
}

const getStatusChip = (Executed_at: string | null) =>
  Executed_at ? (
    <Chip color='success' label='Ausgeführt' size='small' />
  ) : (
    <Chip color='default' label='Offen' size='small' variant='outlined' />
  );

export const LegalDependencyOverview = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [includeExecuted, setIncludeExecuted] = useState(false);
  const {
    legalRequirementDependencyOverview,
    isLoadingLegalRequirementDependencyOverview,
    isFetchingLegalRequirementDependencyOverview,
  } = useGetLegalRequirementDependencyOverview(
    page + 1,
    rowsPerPage,
    search,
    includeExecuted
  );

  const debouncedSetSearch = useMemo(
    () =>
      debounce((nextSearch: string) => {
        setPage(0);
        setSearch(nextSearch.trim());
      }, 400),
    []
  );

  const rows = useMemo<ILegalDependencyOverviewRow[]>(() => {
    if (!legalRequirementDependencyOverview) {
      return [];
    }

    return [...legalRequirementDependencyOverview.results]
      .sort((a, b) => b.Rule.idLRDRule - a.Rule.idLRDRule)
      .map((entry: ILegalRequirementDependencyOverviewEntry) => {
        const rule = entry.Rule;
        const rootTransactionId = getRuleTransactionId(rule);
        const rootTransactionLabel =
          rootTransactionId !== null
            ? `Vorgang #${rootTransactionId}`
            : 'Unbekannter Vorgang';
        const groups = entry.ConditionGroupArray ?? [];
        const conditionsByGroupId = new Map<
          number,
          ILegalRequirementDependencyCondition[]
        >();
        const actionsByRuleId = new Map<
          number,
          ILegalRequirementDependencyAction[]
        >();

        entry.ConditionArray.forEach(
          (condition: ILegalRequirementDependencyCondition) => {
            const groupId = getConditionGroupId(condition);

            if (groupId === null) {
              return;
            }

            const conditions = conditionsByGroupId.get(groupId) || [];
            conditions.push(condition);
            conditionsByGroupId.set(groupId, conditions);
          }
        );

        entry.ActionArray.forEach(
          (action: ILegalRequirementDependencyAction) => {
            const actionRuleId = getActionRuleId(action);

            if (actionRuleId === null) {
              return;
            }

            const actions = actionsByRuleId.get(actionRuleId) || [];
            actions.push(action);
            actionsByRuleId.set(actionRuleId, actions);
          }
        );

        const conditionCount = groups.reduce(
          (sum, group) =>
            sum +
            (conditionsByGroupId.get(group.idLRDConditionGroup)?.length || 0),
          0
        );
        const actionCount = (actionsByRuleId.get(rule.idLRDRule) || []).length;

        return {
          id: rule.idLRDRule,
          actionCount,
          conditionCount,
          Created_at: rule.Created_at ?? null,
          Executed_at: rule.Executed_at ?? null,
          groupCount: groups.length,
          name: getRuleName(rule) || `Regel #${rule.idLRDRule}`,
          rootTransactionId,
          rootTransactionLabel,
          ruleId: rule.idLRDRule,
          statusLabel: rule.Executed_at ? 'Ausgeführt' : 'Offen',
          Updated_at: rule.Updated_at ?? null,
        };
      });
  }, [legalRequirementDependencyOverview]);

  useEffect(() => {
    return () => {
      debouncedSetSearch.clear();
    };
  }, [debouncedSetSearch]);

  useEffect(() => {
    const totalCount = legalRequirementDependencyOverview?.count ?? 0;
    const maxPage =
      totalCount > 0 ? Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1) : 0;

    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [legalRequirementDependencyOverview?.count, page, rowsPerPage]);

  const columns = useMemo<GridColDef<ILegalDependencyOverviewRow>[]>(
    () => [
      { field: 'ruleId', headerName: 'Nr.', width: 80 },
      { field: 'name', headerName: 'Regelname', flex: 1.2, minWidth: 220 },
      {
        field: 'rootTransactionLabel',
        headerName: 'Vorgang',
        flex: 1,
        minWidth: 220,
      },
      {
        field: 'statusLabel',
        headerName: 'Status',
        width: 120,
        renderCell: params => getStatusChip(params.row.Executed_at),
      },
      { field: 'groupCount', headerName: 'Gruppen', width: 100 },
      { field: 'conditionCount', headerName: 'Bedingungen', width: 120 },
      { field: 'actionCount', headerName: 'Aktionen', width: 100 },
      {
        field: 'Created_at',
        headerName: 'Erstellt am',
        width: 140,
        renderCell: params => formatDate(params.row.Created_at),
      },
      {
        field: 'Updated_at',
        headerName: 'Aktualisiert am',
        width: 140,
        renderCell: params => formatDate(params.row.Updated_at),
      },
      {
        field: 'Executed_at',
        headerName: 'Ausgeführt am',
        width: 140,
        renderCell: params =>
          params.row.Executed_at ? formatDate(params.row.Executed_at) : '-',
      },
      {
        field: 'actions',
        headerName: 'Aktionen',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: params => (
          <Stack direction='row' spacing={0.5}>
            <Tooltip title='Vorgang öffnen'>
              <span>
                <IconButton
                  disabled={params.row.rootTransactionId === null}
                  onClick={event => {
                    event.stopPropagation();

                    if (params.row.rootTransactionId === null) {
                      return;
                    }

                    navigate(
                      `/legaltransaction/${params.row.rootTransactionId}`
                    );
                  }}
                  size='small'
                >
                  <OpenInNew fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title='Regel bearbeiten'>
              <span>
                <IconButton
                  disabled={params.row.rootTransactionId === null}
                  onClick={event => {
                    event.stopPropagation();

                    if (params.row.rootTransactionId === null) {
                      return;
                    }

                    navigate(
                      `/legaltransaction/${params.row.rootTransactionId}`,
                      {
                        state: {
                          openLegalDependencyEditor: true,
                          selectedDependencyRuleId: params.row.ruleId,
                        },
                      }
                    );
                  }}
                  size='small'
                >
                  <Edit fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [navigate]
  );

  if (
    !isLoadingLegalRequirementDependencyOverview &&
    !legalRequirementDependencyOverview
  ) {
    return (
      <Alert severity='error'>Fehler beim Laden der Abhängigkeiten.</Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ mr: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant='h4'>Abhängigkeiten</Typography>
          <Chip
            icon={<AccountTree />}
            label={`${
              legalRequirementDependencyOverview?.count ?? 0
            } Regel${(legalRequirementDependencyOverview?.count ?? 0) === 1 ? '' : 'n'}`}
            variant='outlined'
          />
        </Box>

        <Grid container spacing={2} sx={{ mt: 4 }}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label='Suche'
              onChange={event => {
                const nextValue = event.target.value;
                setSearchInput(nextValue);
                debouncedSetSearch(nextValue);
              }}
              placeholder='Nach Regelname oder Vorgang suchen'
              size='small'
              value={searchInput}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                alignItems: 'center',
                height: '100%',
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={includeExecuted}
                    onChange={event => {
                      setPage(0);
                      setIncludeExecuted(event.target.checked);
                    }}
                  />
                }
                label='Auch geschlossene'
              />
            </Box>
          </Grid>
        </Grid>

        <Box mt={6} mb={1}>{`${rows.length} von ${
          legalRequirementDependencyOverview?.count ?? 0
        } Regeln`}</Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <DataGrid
              autoHeight
              columns={columns}
              disableRowSelectionOnClick
              loading={
                isLoadingLegalRequirementDependencyOverview ||
                isFetchingLegalRequirementDependencyOverview
              }
              localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
              onPaginationModelChange={model => {
                const nextRowsPerPage = model.pageSize;
                const nextPage =
                  nextRowsPerPage !== rowsPerPage ? 0 : model.page;

                if (nextRowsPerPage !== rowsPerPage) {
                  setRowsPerPage(nextRowsPerPage);
                }
                if (nextPage !== page) {
                  setPage(nextPage);
                }
              }}
              pagination
              paginationMode='server'
              paginationModel={{ page, pageSize: rowsPerPage }}
              pageSizeOptions={[25, 50, 100]}
              rowCount={legalRequirementDependencyOverview?.count ?? 0}
              rows={rows}
              sx={{ bgcolor: 'background.paper' }}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
